import { Button } from '@99designs/common/components/Button';
import { ButtonGroup, ButtonGroupItem } from '@99designs/common/components/ButtonGroup';
import { Element, Footer as FooterBar } from 'common/components/FooterBar';
import ProgressBar from 'common/components/ProgressBar';
import Responsive from 'common/components/Responsive';
import Portal from 'projects/components/Portal';
import { Color } from 'projects/consts';
import { StepState } from 'projects/util/step';
import React, { useContext, useEffect } from 'react';
import ColorComponent from './Color';
import { LayoutContext } from './Layout';

export interface Props<T> {
    step: StepState<T>;
    isAuthenticated: boolean;
    nextInProgress: boolean;
    nextAction: () => void;
    nextActionLabel: string;
    launchAction: () => void;
    disabled?: boolean;
}

export const StepFooterButton: React.SFC<{
    variations: string[];
    onClick: () => void;
    disabled?: boolean;
    saving?: boolean;
}> = props => (
    <Button
        variations={props.variations}
        iconAlignment="right"
        isDisabled={props.disabled}
        isSaving={props.saving}
        onClick={props.onClick}
    >
        {props.children}
    </Button>
);

function StepFooterActions<T>({
    nextAction,
    nextActionLabel,
    disabled = false,
    nextInProgress,
}: Props<T>) {
    return (
        <>
            <Responsive min="normal">
                <ButtonGroup variations={['responsive-fluid']}>
                    <ButtonGroupItem>
                        <StepFooterButton
                            disabled={disabled}
                            variations={[
                                'primary',
                                'nowrap',
                                'chameleon',
                                'responsive',
                                'jumbo',
                                'fluid',
                            ]}
                            onClick={nextAction}
                            saving={nextInProgress}
                        >
                            {nextActionLabel}
                        </StepFooterButton>
                    </ButtonGroupItem>
                </ButtonGroup>
            </Responsive>
            <Responsive max="medium">
                <ButtonGroup variations={['fluid']}>
                    <ButtonGroupItem>
                        <StepFooterButton
                            disabled={disabled}
                            variations={['primary', 'nowrap', 'responsive', 'chameleon', 'fluid']}
                            onClick={nextAction}
                            saving={nextInProgress}
                        >
                            {nextActionLabel === __('Confirm designers')
                                ? __('Confirm')
                                : nextActionLabel}
                        </StepFooterButton>
                    </ButtonGroupItem>
                </ButtonGroup>
            </Responsive>
        </>
    );
}

function StepFooter<T>(props: React.PropsWithChildren<Props<T>>) {
    const { children, step } = props;
    const { footerRef } = useContext(LayoutContext);
    const ref = React.createRef<HTMLDivElement>();

    useEffect(() => {
        if (footerRef.current && ref.current) {
            footerRef.current.style.marginBottom = `${ref.current.clientHeight}px`;
        }

        return () => {
            if (footerRef.current) {
                footerRef.current.style.marginBottom = '0px';
            }
        };
    });

    return (
        <Portal>
            <ColorComponent color={Color.createBlue}>
                <Responsive min="normal">
                    <FooterBar
                        ref={ref}
                        top={
                            <ProgressBar
                                color={Color.createBlue}
                                variations={['fluid']}
                                percentageComplete={step.percentageComplete()}
                            />
                        }
                    >
                        <Element align="left" cols={6}>
                            {children}
                        </Element>
                        <Element align="right" cols={6}>
                            <StepFooterActions {...props} />
                        </Element>
                    </FooterBar>
                </Responsive>
                <Responsive max="medium">
                    <FooterBar position="sticky" ref={ref}>
                        <Element align="left" cols={6}>
                            {children}
                        </Element>
                        <Element align="right" cols={6}>
                            <StepFooterActions {...props} />
                        </Element>
                    </FooterBar>
                </Responsive>
            </ColorComponent>
        </Portal>
    );
}

export default StepFooter;
